import { ADDRESS_NOT_AVAILABLE_LABEL, SQFT_NOT_AVAILABLE_LABEL, type ListingCardData } from 'components/listing-card/listing-card-v2';
import { cleanDashedString } from 'data/addresses';
import Listing from 'data/listing/client-app/types/listing';
import { isExpired, isSold } from 'types/reso';
import { getProviderLogoUrl } from 'utils/compliance';
import generateFullAddress from 'utils/get-full-address';
import { isUsStateCode, provinceOrStateCodeFromString } from 'utils/province_or_state';
import { isQuebecListing } from 'utils/is-quebec-listing';

/**
 * View Model for transforming Listing (from SavedListingFull) to ListingCard format
 * 
 * Pattern: Follows the same structure as SimilarListingViewModel for consistency.
 * Works with the Listing class from client-app API.
 * Compliance data is handled externally by transformer functions.
 * 
 * Reference: src/components/pages/address/public/components/similar-listings-sections/similar-listing-view-model.ts
 */
export class SavedListingViewModel implements ListingCardData {
  constructor(private listing: Listing) {}

  get id() {
    return this.listing.id;
  }

  get thumbnailSrc() {
    // Use imageUrl if available, otherwise thumbnailUrl
    return this.listing.imageUrl || this.listing.thumbnailUrl || null;
  }

  get thumbnailAlt() {
    return this.listing.imageDesc || this.street || ADDRESS_NOT_AVAILABLE_LABEL;
  }

  get href() {
    if (!this.listing.displayAddress) {
      return this.listing.addressPath;
    }
    return cleanDashedString(this.listing.addressPath);
  }

  get fullAddress() {
    const fullAddress = generateFullAddress(this.street, this.city, this.province, false);
    if (fullAddress) {
      return fullAddress;
    }
    return ADDRESS_NOT_AVAILABLE_LABEL;
  }

  get street() {
    const { streetNumber, streetName, unitNumber } = this.listing;
    const streetAddress = streetNumber ? `${streetNumber} ${streetName}` : streetName;
    const fullAddress = unitNumber?.length > 0 ? `${unitNumber} - ${streetAddress}` : streetAddress;
    return fullAddress;
  }

  get city() {
    return this.listing.city;
  }

  get province() {
    return this.listing.province;
  }

  get timestamp(): Date | null {
    // Quebec listings must not display timestamps for regional compliance
    if (isQuebecListing(this.listing.province)) {
      return null;
    }
    
    const standardStatus = this.listing.standardStatus;
    
    if (isSold(standardStatus)) {
      return this.listing.soldAt || this.listing.updatedOn || this.listing.addedAt;
    } else if (isExpired(standardStatus)) {
      return this.listing.updatedOn || this.listing.addedAt;
    }
    return this.listing.addedAt;
  }

  get price() {
    return this.listing.price;
  }

  get soldPrice() {
    return this.listing.soldPrice;
  }

  get standardStatus() {
    return this.listing.standardStatus;
  }

  get onMarketDate() {
    return this.listing.onMarketDate ?? null;
  }

  get isRental() {
    return this.listing.isRental ?? false;
  }

  get providerId() {
    return this.listing.providerId;
  }

  get providerLogo() {
    /* At the moment, we only have the provider logo for US listings */
    if (isUsStateCode(provinceOrStateCodeFromString(this.listing?.province))) {
      return getProviderLogoUrl(this.listing.providerId);
    }
    return null;
  }

  get mlsNum() {
    return this.listing.mlsNum;
  }

  get brokerage() {
    return this.listing.brokerage;
  }

  get position() {
    if (this.listing.position) {
      return {
        latitude: this.listing.position.latitude,
        longitude: this.listing.position.longitude,
      };
    }
    return null;
  }

  get bedrooms() {
    return this.listing.bedrooms ?? null;
  }

  get bathrooms() {
    return this.listing.bathrooms ?? null;
  }

  get avgSqft() {
    if (this.listing.squareFootage) {
      const { min, max } = this.listing.squareFootage;
      if (min && max) {
        return Math.ceil((min + max) / 2);
      } else if (min) {
        return min;
      } else if (max) {
        return max;
      }
    }
    return null;
  }

  get sqftLabel() {
    if (this.listing.squareFootage) {
      const { min, max } = this.listing.squareFootage;
      
      // Format sqft range similar to SimilarListingViewModel
      // Note: if max sqft is 499 always show 0-499, for anything else show min-max or just min or max when only one available
      const sqft = max === 499 ? '0-499' : [min, max].filter(item => item).join('-');
      
      return !sqft?.trim()?.length ? SQFT_NOT_AVAILABLE_LABEL : sqft.trim();
    }
    return SQFT_NOT_AVAILABLE_LABEL;
  }

  toJSON() {
    return {
      id: this.id ?? null,
      thumbnailSrc: this.thumbnailSrc ?? null,
      thumbnailAlt: this.thumbnailAlt ?? null,
      href: this.href ?? null,
      fullAddress: this.fullAddress ?? null,
      street: this.street ?? null,
      city: this.city ?? null,
      province: this.province ?? null,
      timestamp: this.timestamp instanceof Date ? this.timestamp.toISOString() : (this.timestamp ?? null),
      price: this.price ?? null,
      soldPrice: this.soldPrice ?? null,
      standardStatus: this.standardStatus ?? null,
      onMarketDate: (this.onMarketDate instanceof Date ? this.onMarketDate.toISOString() : this.onMarketDate) ?? null,
      isRental: this.isRental ?? null,
      providerId: this.providerId ?? null,
      providerLogo: this.providerLogo ?? null,
      mlsNum: this.mlsNum ?? null,
      brokerage: this.brokerage ?? null,
      position: this.position ?? null,
      bedrooms: this.bedrooms ?? null,
      bathrooms: this.bathrooms ?? null,
      avgSqft: this.avgSqft ?? null,
      sqftLabel: this.sqftLabel ?? null,
    };
  }
}

